import { useSentryRoutes } from './telemetry';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { CacheProvider } from '@emotion/react';

import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import createTheme from './theme';
import routes from './routes';

import useTheme from './hooks/useTheme';
import createEmotionCache from './utils/createEmotionCache';

import { useAppShellStore, useSessionStore } from './stores';
import { EmotionCache } from '@emotion/cache';

const clientSideEmotionCache = createEmotionCache();

type AppProps = {
  emotionCache?: EmotionCache;
};

function App({ emotionCache = clientSideEmotionCache }: AppProps) {
  const skimmerName = 'Skimmer';
  const content = useSentryRoutes(routes);
  const location = useLocation();
  const { theme } = useTheme();

  const refreshContext = useSessionStore.use.refreshContext();
  const setIsInitialized = useAppShellStore.use.setIsInitialized();
  const companyName = useSessionStore.use.companyName();
  const domainNotFound = location.pathname.includes('domain-not-found');

  useEffect(() => {
    setIsInitialized(false);
    if (domainNotFound) {
      setIsInitialized(true);
    } else {
      refreshContext().then(() => {
        setIsInitialized(true);
      });
    }
  }, [refreshContext, setIsInitialized]);

  return (
    <CacheProvider value={emotionCache}>
      <HelmetProvider>
        <Helmet titleTemplate={`%s | ${companyName || skimmerName}`} defaultTitle={`${companyName || skimmerName} Customer Portal`} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MuiThemeProvider theme={createTheme(theme)}>{content}</MuiThemeProvider>
        </LocalizationProvider>
      </HelmetProvider>
    </CacheProvider>
  );
}

export default App;
